<template>
  <v-card outlined class="pa-4 mb-lg-16 d-flex mx-4 rounded-lg mt-10" ref="tableCard">
    <v-data-table
      :headers="headers"
      fixed-header
      :items="items"
      :options="localOptions"
      :loading="loading"
      :server-items-length="totalData"
      loading-text="Loading... Please wait"
      :footer-props="footerProps"
      :item-key="effectiveItemKey"
      :class="tableClass"
      :style="{ padding: '10px', height: `${10 * 55 + 56 + 56}px`, width: '100%' }"
      :mobile-breakpoint="0"
      :expanded.sync="expandedItems"
      @update:options="updateOptions"
    >  
      <template v-slot:top>
        <div v-if="!customTopActions">
          <TableTop :loading="loading" :refresh-table="refreshTable" />
        </div>
        <div v-else>
          <slot name="top" />
        </div>
      </template>

      <template v-if="bodyFilter" v-slot:[`body.prepend`]>
        <slot name="body-prepend"/>
      </template>
      
      <template
        v-for="header in headersWithCustomHeader"
        v-slot:[`header.${header.value}`]="slotProps"
      >
        <slot :name="`header.${header.value}`" v-bind="slotProps" />
      </template>

      <template v-slot:item="{ item, index }">
        <TableItem
          :item="item"
          :index="index"
          :headers="headers"
          :options="localOptions"
          :selectedRow="selectedRow"
          :expandedItem="expandedItem"
          :isExpanded="expandedKeys.includes(item[effectiveItemKey])"
          @select-row="selectRow"
          @view-detail="viewDetail"
          @toggle-expand="toggleExpand"
        >
          <template v-slot:action="{ item }">
            <slot name="action" :item="item" :index="index" />
          </template>
          <template v-slot:custom="{ item }">
            <slot name="custom" :item="item" :index="index" />
          </template>
          <template v-slot:custom_status="{ item }">
            <slot name="custom_status" :item="item" :index="index" />
          </template>
          <template
            v-for="header in headers"
            v-slot:[header.value]="slotProps"
          >
            <slot :name="header.value" v-bind="slotProps" />
          </template>
          <template
            v-for="header in headers"
            v-slot:[`tooltip__${header.value}`]="slotProps"
          >
          <slot :name="`tooltip__${header.value}`" v-bind="slotProps" />
        </template>
        </TableItem>
      </template>

      <template v-if="expandedItem" v-slot:expanded-item="{ item }">
        <slot name="expanded-item" :item="item" />
      </template>
    </v-data-table>

    <v-btn
      v-if="showFloatingButton"
      id="floating-arrow"
      fab
      small
      color="primary"
      class="floating-arrow"
      @click="scrollTable"
    >
      <v-icon :class="{ 'rotate-arrow': isAtEnd }">
        mdi-arrow-right
      </v-icon>
    </v-btn>
  </v-card>
</template>

<script>
import TableItem from './TableItem.vue';

export default {
  name: 'TableView',
  components: {
    TableItem,
    TableTop: () => import("./TableTop.vue"),
  },
  props: {
    bodyFilter: { type: Boolean, default: false },
    headers: { type: Array, required: true, default: () => [] },
    items: { type: Array, required: true, default: () => [] },
    options: { type: Object, required: true, default: () => ({}) },
    loading: { type: Boolean, default: false },
    footerProps: { type: Object, default: () => ({ itemsPerPageOptions: [5, 10, 25, 50], showFirstLastPage: false }) },
    itemKey: { type: String, default: 'id' },
    tableHeight: { type: String, default: '100%' },
    selectedItems: { type: Array, default: () => [] },
    tableClass: { type: String, default: 'd-print-table d-flex flex-column flex-1 pa-0 ma-0 h-100' },
    totalData: { type: Number, default: 0 },
    refreshTable: { type: Function, default: () => {} },
    customTopActions: { type: Boolean, default: false },
    expandedItem: { type: Boolean, default: false },
    expandWithApi: { type: Boolean, default: false }
  },
  data() {
    return {
      localOptions: { ...this.options },
      selectedRow: null,
      isAtEnd: false,
      showFloatingButton: false,
      expandedKeys: []
    };
  },
  computed: {
    effectiveItemKey() {
      return this.itemKey || '_id' || 'id';
    },
    headersWithCustomHeader() {
      return this.headers.filter(
        (h) => !!this.$scopedSlots[`header.${h.value}`]
      );
    },
    expandedItems: {
      get() {
        return this.items.filter(item => 
          this.expandedKeys.includes(item[this.effectiveItemKey])
        );
      },
      set(val) {
        this.expandedKeys = val.map(item => item[this.effectiveItemKey]);
      }
    }
  },
  watch: {
    options: {
      immediate: true,  
      deep: true,
      handler(newOptions) {
        this.localOptions = { ...newOptions };
      }
    },
    items() {
      this.expandedKeys = [];
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.checkScrollPosition();
      const tableWrapper = this.$el.querySelector('.v-data-table__wrapper');
      if (tableWrapper) {
        tableWrapper.addEventListener('scroll', this.checkScrollPosition);
      }
    });
  },
  methods: {
    updateOptions(newOptions) {
      this.$emit('update:options', newOptions);
    },
    selectRow(index) {
      this.selectedRow = index;
    },
    viewDetail(id) {
      this.$emit('view-detail', id);
    },
    toggleExpand(index) {
      const item = this.items[index];
      if (item) {
        const itemKey = item[this.effectiveItemKey];
        const expandedIndex = this.expandedKeys.indexOf(itemKey);
        if (expandedIndex > -1) {
          this.expandedKeys.splice(expandedIndex, 1);
        } else {
          this.expandedKeys.push(itemKey);
          if (this.expandWithApi) {
            this.$emit('expand-row', itemKey);
          }
        }
      }
    },
    checkScrollPosition() {
      this.$nextTick(() => {
        const tableWrapper = this.$el.querySelector('.v-data-table__wrapper');
        if (!tableWrapper) return;

        const { scrollLeft, scrollWidth, clientWidth } = tableWrapper;
        const maxScrollLeft = scrollWidth - clientWidth;

        this.showFloatingButton = scrollWidth > clientWidth;
        this.isAtEnd = scrollLeft >= maxScrollLeft - 10;
      });
    },
    scrollTable() {
      const tableWrapper = this.$el.querySelector('.v-data-table__wrapper');
      if (!tableWrapper) return;

      const maxScrollLeft = tableWrapper.scrollWidth - tableWrapper.clientWidth;

      if (this.isAtEnd) {
        tableWrapper.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        tableWrapper.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
      }
    }
  }
};
</script>

<style scoped>
::v-deep .v-data-table-header th {
  overflow: auto;
  white-space: nowrap;
  background-color: #378595 !important;
  color: white !important;
  padding-bottom: 8px;
  font-weight: 500;
  font-size: 16px !important;
}
.v-card {
  height: 100%;
}
::v-deep .v-data-footer {
  flex-wrap: wrap; 
  justify-content: space-between; 
}

@media (max-width: 600px) {
  ::v-deep .v-data-footer {
    flex-direction: row; 
    align-items: center;
    justify-content: space-between;
  }
  ::v-deep .v-data-footer__icons-before,
  ::v-deep .v-data-footer__icons-after {
    margin: 0; 
  }
  ::v-deep .v-data-footer__pagination {
    flex: 1; 
    text-align: center; 
  }
}

.floating-arrow {
  position: absolute;
  top: 50%;
  right: 5px;
  transform: translateY(-50%);
  z-index: 10;
  opacity: 0.7;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  transition: opacity 0.3s, transform 0.3s ease-in-out;
}
.floating-arrow:hover {
  opacity: 1;
  transform: translateY(-50%) scale(1.1);
}

.rotate-arrow {
  transition: transform 0.3s ease-in-out;
  transform: rotate(180deg);
}
</style>
